import React from 'react';
import { CloudDownload, Trash2, XCircle } from 'lucide-react';
import { LIMIT_OPTIONS, RANGE_PRESETS, fromInputDate, toInputDate, viewingLabel } from '../../lib/callHistory.js';
import AgentMultiSelect from './AgentMultiSelect.jsx';

const field = 'w-full bg-civix-panelLight border border-civix-border rounded-md px-3 py-2 text-sm';
const labelCls = 'block text-[11px] text-white/50 mb-1';

/**
 * Top control bar: agent tags, load size, direction, CSV/bulk-delete buttons,
 * DAY/WEEK/MONTH/CUSTOM presets and start/end pickers.
 * @param {{filters: object, onChange: (patch: object) => void, agents: object[],
 *   selectedCount: number, onDownload: () => void, onBulkDelete: () => void}} props
 * @returns {JSX.Element}
 */
export default function CallHistoryControls({ filters, onChange, agents, selectedCount, onDownload, onBulkDelete }) {
  const { agentIds, limit, direction, preset, startDate, endDate } = filters;
  const clear = (key) => onChange({ [key]: '', preset: 'custom' });
  const dateInput = (key, edge, text) => (
    <div>
      <label className={labelCls} htmlFor={`ch-${key}`}>{text}</label>
      <div className="relative">
        <input
          id={`ch-${key}`}
          type="date"
          value={toInputDate(filters[key])}
          onChange={(e) => onChange({ [key]: fromInputDate(e.target.value, edge), preset: 'custom' })}
          className={`${field} pr-9 w-[170px]`}
        />
        {filters[key] && (
          <button type="button" onClick={() => clear(key)} aria-label={`Clear ${text.toLowerCase()} date`} className="absolute right-2 top-1/2 -translate-y-1/2 text-rose-400">
            <XCircle className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
  return (
    <div className="panel p-4 flex flex-wrap items-start justify-between gap-4">
      <div className="space-y-4">
        <div>
          <label className={labelCls}>Agents</label>
          <AgentMultiSelect agents={agents} value={agentIds} onChange={(ids) => onChange({ agentIds: ids })} />
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className={labelCls} htmlFor="ch-limit">Num calls to load at a time</label>
            <select id="ch-limit" value={limit} onChange={(e) => onChange({ limit: Number(e.target.value) })} className={`${field} w-[170px]`}>
              {LIMIT_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls} htmlFor="ch-dir">Filter by call direction</label>
            <select id="ch-dir" value={direction} onChange={(e) => onChange({ direction: e.target.value })} className={`${field} w-[230px]`}>
              <option value="">Select call direction</option>
              <option value="inbound">Inbound</option>
              <option value="outbound">Outbound</option>
            </select>
          </div>
          <div className="inline-flex rounded-md border border-civix-purple/60 overflow-hidden">
            <button type="button" onClick={onDownload} title="Download CSV" aria-label="Download CSV" className="px-3 py-2 text-civix-purpleSoft hover:bg-civix-purple/10">
              <CloudDownload className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={onBulkDelete}
              disabled={!selectedCount}
              title="Delete selected"
              aria-label="Delete selected"
              className="px-3 py-2 border-l border-civix-purple/60 text-civix-purpleSoft hover:bg-civix-purple/10 disabled:opacity-40"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="inline-flex rounded-md overflow-hidden bg-civix-purple/90" role="group" aria-label="Date range">
          {RANGE_PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => onChange({ preset: p })}
              aria-pressed={preset === p}
              className={`px-4 py-2 text-sm font-semibold tracking-wide uppercase ${preset === p ? 'bg-violet-900/80 text-white' : 'text-white/90 hover:bg-white/10'}`}
            >
              {p}
            </button>
          ))}
        </div>
        <div className="flex gap-3">
          {dateInput('startDate', 'start', 'Start')}
          {dateInput('endDate', 'end', 'End')}
        </div>
        <div className="text-right text-xs text-white/70" data-testid="viewing-label">{viewingLabel(startDate, endDate)}</div>
      </div>
    </div>
  );
}
